/**
 * LoadingSpinner Component
 * 
 * Displays a loading indicator with optional message.
 * Used during async operations like frame loading, photo processing, etc.
 * 
 * Requirements: 7.4, 9.3
 */

export interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Optional message to display below spinner */
  message?: string;
  /** Whether to show as full-screen overlay */
  fullScreen?: boolean;
}

/**
 * LoadingSpinner component - Visual loading indicator
 */
export function LoadingSpinner({
  size = 'md',
  message,
  fullScreen = false,
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'w-6 h-6 border-2',
    md: 'w-12 h-12 border-3',
    lg: 'w-16 h-16 border-4',
    xl: 'w-24 h-24 border-4',
  };

  const spinner = (
    <div className="flex flex-col items-center justify-center gap-4">
      <div
        className={`
          ${sizeClasses[size]}
          border-yellow-500
          border-t-transparent
          rounded-full
          animate-spin
        `}
        data-testid="loading-spinner"
        role="status"
        aria-label="Loading"
      />
      {message && (
        <p className="text-gray-600 text-center font-medium" data-testid="loading-message">
          {message}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div
        className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50"
        data-testid="loading-spinner-fullscreen"
      >
        {spinner}
      </div>
    );
  }

  return spinner;
}
